import useMediaQuery from "@mui/material/useMediaQuery";
import { useIsClient } from "@uidotdev/usehooks";
import { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  companyVideoPosterFromUrl,
  companyVideoUrl,
  isCompanyVideoUrl,
} from "../../../../helpers/company_video";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import SingleJobMediaModal from "../SingleJobMediaModal/SingleJobMediaModal";
import {
  GridStyled,
  ImageStyled,
  OutlinedButton,
  PlayIconStyled,
  ReactPlayerStyled,
  ShowAllMediaButton,
  ShowAllMediaIcon,
} from "./styles";


export default function SingleJobMedia(props) {
  const isClient = useIsClient();
  const { t } = useTranslation();
  const belowLg = useMediaQuery("(max-width: 1279px)");
  const belowTablet = useMediaQuery("(max-width: 640px)");

  const { job } = props;
  const { profile } = job?.company || {};
  const { images, videos } = profile || {};

  // START - Processing and formatting media data structure
  const formattedVideoUrls =
    Array.isArray(videos) && videos.length > 0
      ? videos.map((video) =>
          companyVideoUrl(video?.vimeoId)
        )
      : [];

  const formattedImageUrls =
    Array.isArray(images) && images.length > 0
      ? images.map((companyImg) => (companyImg.image ? companyImg.image : ""))
      : [];

  const noOfImages =
    5 - formattedVideoUrls.length < 0 ? 0 : 5 - formattedVideoUrls.length;

  const combinedImageAndVideo = formattedVideoUrls.concat(
    formattedImageUrls.slice(0, noOfImages)
  );

  const combinedMediaForModal =
    formattedVideoUrls.concat(formattedImageUrls) ?? [];

  // END - Processing and formatting media data structure

  function conditionalRenderMedia() {
    const removeFirstItem = Array.isArray(combinedImageAndVideo)
      ? combinedImageAndVideo.slice(1)
      : [];

    // index 0 and 2 should stretch
    // if length of removeFirstItem is odd
    const stretchOddIndex = removeFirstItem.length % 2 !== 0 ? true : false;

    return removeFirstItem.map((mediaUrl, index) => {
      if (typeof mediaUrl === "string" && mediaUrl) {
        if (isCompanyVideoUrl(mediaUrl)) {
          return (
            <ReactPlayerStyled
              url={mediaUrl ?? ""}
              ref={
                index === 0
                  ? videoTwoRef
                  : index === 1
                  ? videoThreeRef
                  : fallbackVideoRef
              }
              playing={false}
              width={"100%"}
              height={"100%"}
              light={companyVideoPosterFromUrl(mediaUrl)}
              playIcon={<PlayIconStyled />}
              style={{
                borderRadius: "10px",
                minWidth: belowTablet ? "300px" : null,
                // stretch the media if list length is odd number, and this is the only media
                gridColumn: stretchOddIndex && index === 0 ? "span 2" : null,
              }}
              onClickPreview={() => {
                sendTrackingEvent({
                  event: "CE_click-media-ijp",
                  "company-id": job?.company?.id,
                  "job-id": job?.id,
                });
                handleClickMedia(index + 1);
                if (index === 0) {
                  videoTwoRef.current.showPreview();
                } else if (index === 1) {
                  videoThreeRef.current.showPreview();
                } else {
                  fallbackVideoRef.current.showPreview();
                }
              }}
            />
          );
        } else {
          return (
            <GridStyled
              height={"100%"}
              sx={{
                // if its the last item in the list, and it is an odd number, stretch it
                overflow: "hidden",
                borderRadius: "10px",
                gridColumn:
                  stretchOddIndex && index === removeFirstItem.length - 1
                    ? "span 2"
                    : null,
              }}
            >
              <ImageStyled
                draggable={false}
                src={mediaUrl}
                onClick={() => {
                  sendTrackingEvent({
                    event: "CE_click-media-ijp",
                    "company-id": job?.company?.id,
                    "job-id": job?.id,
                  });
                  handleClickMedia(index + 1);
                }}
                alt={`company-image-${index + 1}-media-section`}
              />
            </GridStyled>
          );
        }
      } else {
        return null;
      }
    });
  }

  // Responsible for showing the correct indexed media on the modal popup
  function handleClickMedia(mediaIndex) {
    setOpenModal(true);
    setCurrentMediaIndex(mediaIndex);
  }

  // force CSR with a state
  const [currentMediaIndex, setCurrentMediaIndex] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const videoOneRef = useRef("video1");
  const videoTwoRef = useRef("video2");
  const videoThreeRef = useRef("video3");
  const fallbackVideoRef = useRef("fallbackRef");

  return job?.category !== "aggregated" &&
    isClient &&
    combinedImageAndVideo.length > 0 ? (
    <Fragment>
      <GridStyled
        container
        position={"relative"}
        flexWrap={"nowrap"}
        columns={12}
        marginBottom={belowTablet && "1rem"}
        sx={{
          overflowX: belowTablet ? "scroll" : null,
          overflowY: "clip",
        }}
        columnGap={"10px"}
        maxHeight={belowLg ? "220px" : belowTablet ? "300px" : "370px"}
      >
        <GridStyled item tablet={combinedImageAndVideo.length > 1 ? 6 : 12}>
          {isCompanyVideoUrl(combinedImageAndVideo[0]) ? (
            <ReactPlayerStyled
              key={`job-company-headline-video`}
              url={formattedVideoUrls[0] ?? ""}
              ref={videoOneRef}
              playing={false}
              width={"100%"}
              height={"100%"}
              light={companyVideoPosterFromUrl(formattedVideoUrls[0])}
              playIcon={<PlayIconStyled />}
              style={{
                borderRadius: "10px",
                minWidth: "300px",
                minHeight: !belowLg && !belowTablet ? "370px" : "auto",
              }}
              onClickPreview={() => {
                sendTrackingEvent({
                  event: "CE_click-media-ijp",
                  "company-id": job?.company?.id,
                  "job-id": job?.id,
                });
                handleClickMedia(0);
                videoOneRef.current.showPreview();
              }}
            />
          ) : combinedImageAndVideo[0] ? (
            <ImageStyled
              width={"615px"}
              height={"370px"}
              src={formattedImageUrls[0]}
              draggable={false}
              onClick={() => {
                sendTrackingEvent({
                  event: "CE_click-media-ijp",
                  "company-id": job?.company?.id,
                  "job-id": job?.id,
                });
                handleClickMedia(0);
              }}
              alt={`company-first-image-media-section`}
            />
          ) : null}
        </GridStyled>
        {combinedImageAndVideo.length > 1 ? (
          <GridStyled
            item
            tablet={6}
            display={belowTablet ? "flex" : "grid"}
            gridTemplateColumns={"repeat(2, 1fr)"}
            gridAutoRows={
              belowLg
                ? combinedImageAndVideo.length < 4
                  ? "auto"
                  : "105px"
                : combinedImageAndVideo.length < 4
                ? "auto"
                : "49%"
            }
            gap={"10px"}
          >
            {conditionalRenderMedia()}
          </GridStyled>
        ) : null}
        {!belowTablet ? (
          <ShowAllMediaButton
            onClick={() => {
              sendTrackingEvent({
                event: "CE_click-show-all-media-ijp",
                "company-id": job?.company?.id,
                "job-id": job?.id,
              });
              setCurrentMediaIndex(0);
              setOpenModal(true);
            }}
            id="show-all-media"
          >
            <ShowAllMediaIcon />
            {t("jobs.single_job.show_all_media")}
          </ShowAllMediaButton>
        ) : null}
      </GridStyled>
      {belowTablet ? (
        <OutlinedButton
          variant="outlined"
          onClick={() => {
            setCurrentMediaIndex(0);
            setOpenModal(true);
            sendTrackingEvent({
              event: "CE_click-show-all-media-ijp",
              "company-id": job?.company?.id,
              "job-id": job?.id,
            });
          }}
          id="show-all-media"
        >
          <ShowAllMediaIcon /> {t("jobs.single_job.show_all_media")}
        </OutlinedButton>
      ) : null}
      <SingleJobMediaModal
        media={combinedMediaForModal} // all the media of the company
        currentMediaIndex={currentMediaIndex} // index of the selected media for viewing upon onclick event
        open={openModal} // boolean to toggle whether modal is open or close
        setOpenModal={setOpenModal} // setState to open or close the modal
        handleClickMedia={handleClickMedia} // function to open the modal, and change state of current media index
      />
    </Fragment>
  ) : null;
}
